'use client'

import useCartProduct from '@/app/[locale]/_hooks/useCartProduct'
import productService from '@/app/[locale]/_service/product.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import {
  ADD_TO_CART,
  DECREMENT,
  INCREMENT,
  REMOVE,
} from '@/app/[locale]/_store/cart/cart.slice'
import { IRedux } from '@/app/[locale]/_store/types'
import {
  CurrentVariantType,
  IProduct,
  ISavedModifier,
} from '@/app/[locale]/_types'
import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ComboProducts from './components/ComboProducts'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import { useRouter } from '@/i18n/navigation'
import { Button } from '@/components/ui/button'
import Counter from '../Counter'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import useGTM from '@/app/[locale]/_hooks/useGTM'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'
import { usePathname } from '@/i18n/navigation'
import { track } from '@/app/[locale]/_utils/utm'
import useProductDiscount from '@/app/[locale]/_hooks/useProductDiscount'

const Combo = ({
  data,
  onSuccess,
  onRedirectToCart,
}: {
  data: IProduct
  onSuccess?: () => void
  onRedirectToCart?: () => void
}) => {
  const [quantity, setQuantity] = useState(1)
  const [currentVariant, setCurrentVariant] = useState<CurrentVariantType>({})

  const router = useRouter()
  const t = useTranslations()
  const pathname = usePathname()
  const dispatch = useDispatch()
  const currentLocale = useCustomLocale()

  const { sendGTM } = useGTM()
  const { branch } = useSelector((state: IRedux) => state.common)
  const { data: settingsData } = useSelector((state: IRedux) => state.settings)
  const { menu_id, is_hall } = useSelector((state: IRedux) => state.session)
  const { productInCart } = useCartProduct(data?.id, [], currentVariant)
  const { discounts } = useProductDiscount()

  const price: number = useMemo(() => data?.out_price || 0, [data?.out_price])

  const isHall = is_hall === 'true'

  const selectedMenuId =
    isHall || pathname.endsWith('/menu')
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || ''

  const totalDiscountPrice = useMemo(() => {
    if (data?.discounts?.length) {
      return data.discounts.reduce((acc, cur) => acc + cur.discount_price, 0)
    }

    if (!discounts?.length) return 0

    return discounts.reduce((acc, discount) => {
      if (
        discount.discount_id === 'e46ce26c-5f1b-4ca6-89f1-c013ed5f58ce' &&
        data?.id !== '673c4d0a-1368-40ff-bef5-32c57dbbf964'
      ) {
        return acc
      }
      if (discount.product_and_discount) {
        acc += Number(discount.product_and_discount[data?.id] ?? 0)
      }

      if (discount.discount_mode === 'fixed') {
        acc -= Number(discount.discount_amount || 0)
      }

      if (discount.discount_mode === 'percent') {
        acc -= Number(
          ((data?.out_price || 0) * (discount.discount_amount || 0)) / 100
        )
      }

      return acc
    }, 0)
  }, [data?.discounts, discounts, data?.id, data?.out_price])

  const { useDynamicLanguage: dynamic, isShipperResolved } =
    useDynamicLanguage()

  const { data: comboData } = useQuery({
    queryKey: ['combo', data?.id, dynamic],
    queryFn: () =>
      productService
        .getComboById(
          data?.id,
          {
            menu_id: selectedMenuId,
          },
          dynamic
        )
        .then((res) => res?.data),
    enabled: Boolean(data?.type === 'combo' && data?.id) && isShipperResolved,
  })

  const onOrderClick = () => {
    if (data?.id && data?.type === 'combo') {
      const basePriceWithModifiers = price + additionalPrice
      dispatch(
        ADD_TO_CART({
          key: '',
          comment: '',
          in_stop: false,
          default_product: false,
          discount_percent:
            totalDiscountPrice !== 0 && basePriceWithModifiers > 0
              ? Math.abs(
                  Math.round(
                    (totalDiscountPrice / basePriceWithModifiers) * 100
                  )
                )
              : 0,
          product_name: data.title?.[currentLocale],
          price_with_discount: basePriceWithModifiers + totalDiscountPrice,

          price: basePriceWithModifiers,
          product_id: data?.id,
          type: data?.type,
          variants: currentVariant,
          order_modifiers: [],
          quantity: quantity,
          category_name: data?.categories?.[0]?.title?.[currentLocale],
        })
      )
      track({
        action_type: 'add_to_cart',
      })
      sendGTM('add_to_cart', { product: data, quantity })
      if (onSuccess) {
        onSuccess()
      } else {
        router.push(createUrlWithParams('/'))
      }
    }
  }

  const onRemoveProduct = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
        name: settingsData?.data?.shipper_name,
      })
      dispatch(REMOVE(productInCart?.key))
      setQuantity(1)
    }
  }

  useEffect(() => {
    if (productInCart?.quantity) {
      setQuantity(productInCart?.quantity)
    }
  }, [])

  const additionalPrice = useMemo(
    () =>
      Object.values(currentVariant).reduce((sum, variant) => {
        const modifiersTotal = variant?.order_modifiers?.reduce(
          (acc, modifier) => {
            return acc + modifier.modifier_price * modifier.modifier_quantity
          },
          0
        )

        return sum + modifiersTotal
      }, 0),
    [currentVariant]
  )

  const onAddVariant = (
    group_id: string,
    variant: IProduct,
    mods: ISavedModifier[],
    quantity: number
  ) => {
    setCurrentVariant((prevState) => ({
      ...prevState,
      [group_id]: {
        ...prevState?.[group_id],
        group_id,
        quantity,
        variant_id: variant?.id,
        variant_name: variant?.title?.[currentLocale],
        order_modifiers: mods,
        price: variant?.out_price,
        image: variant?.image,
      },
    }))
  }

  const checkedAllCombos = Object.values(currentVariant)?.some(
    (el) => el?.variant_id === ''
  )

  return (
    <>
      <Box flex={1} mt={6}>
        <Text
          fontSize={{ base: 'xs', lg: 'md' }}
          textTransform={'uppercase'}
          color={'text.muted'}
          fontWeight={500}
          ml={{ base: 4, lg: 0 }}
        >
          {t('assembly_of_the_set')}
        </Text>
        <ComboProducts
          comboData={comboData}
          currentVariant={currentVariant}
          onAddVariant={onAddVariant}
        />
      </Box>
      <Stack
        position='sticky'
        bottom={0}
        left={0}
        bg='background.card'
        zIndex={10}
        w='full'
        py={4}
        px={{ base: 4, lg: 0 }}
        gap={{ base: 4, lg: 6 }}
        borderTop='1px solid #00000014'
        boxShadow={{ base: '0 -4px 6px -2px #00000014', lg: 'none' }}
      >
        <Flex
          w='full'
          justifyContent='space-between'
          fontSize={{ base: 'sm', lg: '2xl' }}
          fontWeight={600}
          color='text'
        >
          <Text fontSize={{ base: 'sm', lg: 'xl' }} fontWeight={500} flex={1}>
            {data?.title?.[currentLocale]}
          </Text>
          <Text as='span'>
            <NumberToPrice
              unstyled
              value={
                productInCart?.quantity
                  ? (price + additionalPrice + totalDiscountPrice) *
                    productInCart.quantity
                  : (price + additionalPrice + totalDiscountPrice) * quantity
              }
            />
          </Text>
        </Flex>
        <Flex gap={4} w='full'>
          <Counter
            height={{ base: '52px', lg: '56px' }}
            variable={
              productInCart?.quantity ? productInCart.quantity : quantity
            }
            onIncrease={() =>
              productInCart?.quantity
                ? dispatch(INCREMENT(productInCart?.key))
                : setQuantity((prevState) => ++prevState)
            }
            onDecrease={() =>
              productInCart?.quantity
                ? productInCart.quantity > 1
                  ? dispatch(DECREMENT(productInCart?.key))
                  : onRemoveProduct()
                : quantity > 1
                ? setQuantity((prevState) => --prevState)
                : setQuantity(1)
            }
            color='text'
          />
          {Boolean(productInCart) ? (
            <Button
              flex={1}
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              rounded={16}
              fontWeight={600}
              h={{ base: '52px', lg: '56px' }}
              fontSize={{ base: 'sm', lg: 'xl' }}
              onClick={onRedirectToCart}
            >
              {t('go_to_cart')}
            </Button>
          ) : (
            <Button
              onClick={() => onOrderClick()}
              disabled={Boolean(
                comboData && comboData?.groups?.length > 0
                  ? checkedAllCombos ||
                      Object.keys(currentVariant)?.length !==
                        comboData?.groups?.length
                  : !data
              )}
              h={{ base: '52px', lg: '56px' }}
              flex={1}
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              rounded={16}
              fontWeight={600}
              fontSize={{ base: 'sm', lg: 'xl' }}
            >
              {t('add_to_cart')}
            </Button>
          )}
        </Flex>
      </Stack>
    </>
  )
}

export default Combo
